import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Grid,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';

const Patients: React.FC = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalPatients, setTotalPatients] = useState(0);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [dateFilter, setDateFilter] = useState('');

  // Add patient form state
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formData, setFormData] = useState({
    opd: '',
    name: '',
    age: '',
    birthdate: '',
    gender: '',
    address: '',
    mobile: '',
    datevisit: new Date().toISOString().split('T')[0],
    // Medical history fields - using boolean values
    has_dm: false,
    has_bp: false,
    has_tsh: false,
    has_acidity: false,
    has_allergic: false,
    allergic_details: '',
    has_others: false,
    others_details: ''
  });

  // Store all patients fetched from API for frontend filtering
  const [allPatients, setAllPatients] = useState<any[]>([]);

  useEffect(() => {
    fetchPatients();
    // eslint-disable-next-line
  }, [searchTerm, sortBy, sortOrder, dateFilter]);

  // Fetch all patients for frontend filtering by last visit date
  const fetchPatients = async () => {
    try {
      setLoading(true);
      // Fetch a large number to get all patients for frontend filtering
      const res = await api.getPatients({
        page: 1,
        limit: 10000, // fetch all for filtering
        search: searchTerm,
        sortBy,
        sortOrder
      });
      setAllPatients(res.data?.patients || []);
      setLoading(false);
    } catch (err) {
      setError('Failed to load patients');
      setLoading(false);
    }
  };

  // Filter patients by date of last visit on frontend
  const getFilteredPatients = () => {
    let filtered = [...allPatients];

    // Date filter by last_visit_date
    if (dateFilter) {
      filtered = filtered.filter((patient) => {
        if (!patient.last_visit_date) return false;
        // Compare only the date part
        const lastVisit = new Date(patient.last_visit_date);
        const filterDate = new Date(dateFilter);
        // Set both to midnight for date-only comparison
        lastVisit.setHours(0, 0, 0, 0);
        filterDate.setHours(0, 0, 0, 0);
        return lastVisit.getTime() === filterDate.getTime();
      });
    }

    // Sorting
    if (sortBy) {
      filtered.sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];
        
        // For last_visit_date, sort as date
        if (sortBy === 'last_visit' || sortBy === 'last_visit_date') {
          aValue = a.last_visit_date ? new Date(a.last_visit_date).getTime() : 0;
          bValue = b.last_visit_date ? new Date(b.last_visit_date).getTime() : 0;
        }
        // For OPD, sort as number
        else if (sortBy === 'opd') {
          aValue = parseInt(aValue) || 0;
          bValue = parseInt(bValue) || 0;
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          if (sortOrder === 'ASC') return aValue.localeCompare(bValue);
          else return bValue.localeCompare(aValue);
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          if (sortOrder === 'ASC') return aValue - bValue;
          else return bValue - aValue;
        }
        if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
          if (sortOrder === 'ASC') return Number(aValue) - Number(bValue);
          else return Number(bValue) - Number(aValue);
        }
        // fallback
        if (sortOrder === 'ASC') return (aValue > bValue ? 1 : -1);
        else return (aValue < bValue ? 1 : -1);
      });
    }

    return filtered;
  };

  // Pagination logic for filtered patients
  const paginatedPatients = () => {
    const filtered = getFilteredPatients();
    return filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  // Update totalPatients when filters change
  useEffect(() => {
    setTotalPatients(getFilteredPatients().length);
    setPage(0); // Reset to first page on filter change
    // eslint-disable-next-line
  }, [dateFilter, rowsPerPage, allPatients]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('DESC');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === 'ASC' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No visits';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const calculateAge = (birthdate: string) => {
    if (!birthdate) return '';
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age.toString();
  };

  const handleBirthdateChange = (birthdate: string) => {
    setFormData(prev => ({
      ...prev,
      birthdate,
      age: birthdate ? calculateAge(birthdate) : prev.age
    }));
  };

  const generateOPDNumber = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `OPD${currentYear}${currentMonth}${randomNum}`;
  };

  const getNextOPDNumber = () => {
    const filtered = getFilteredPatients();
    if (filtered.length === 0) {
      // If no patients exist, start with 1
      return '1';
    }
    // Find the highest numeric OPD number
    const opdNumbers = filtered
      .map(patient => patient.opd)
      .filter(opd => opd && !isNaN(Number(opd)))
      .map(opd => parseInt(opd) || 0);

    if (opdNumbers.length === 0) {
      return '1';
    }

    const maxOPD = Math.max(...opdNumbers);
    const nextOPD = maxOPD + 1;
    return nextOPD.toString();
  };

  const handleAddPatient = () => {
    setFormData({
      opd: getNextOPDNumber(),
      name: '',
      age: '',
      birthdate: '',
      gender: '',
      address: '',
      mobile: '',
      datevisit: new Date().toISOString().split('T')[0],
      // Medical history fields - using boolean values
      has_dm: false,
      has_bp: false,
      has_tsh: false,
      has_acidity: false,
      has_allergic: false,
      allergic_details: '',
      has_others: false,
      others_details: ''
    });
    setOpenAddDialog(true);
  };

  const handleSubmitPatient = async () => {
    // Validate required fields
    if (!formData.name.trim()) {
      setSnackbar({ open: true, message: 'Patient name is required', severity: 'error' });
      return;
    }
    if (!formData.age.trim()) {
      setSnackbar({ open: true, message: 'Age is required', severity: 'error' });
      return;
    }
    if (!formData.gender) {
      setSnackbar({ open: true, message: 'Gender is required', severity: 'error' });
      return;
    }
    if (!formData.datevisit) {
      setSnackbar({ open: true, message: 'Date of first visit is required', severity: 'error' });
      return;
    }

    try {
      await api.createPatient(formData);
      setSnackbar({ open: true, message: 'Patient added successfully', severity: 'success' });
      setOpenAddDialog(false);
      fetchPatients(); // Refresh the list
    } catch (error) {
      console.error('Error adding patient:', error);
      setSnackbar({ open: true, message: 'Failed to add patient', severity: 'error' });
    }
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      opd: '',
      name: '',
      age: '',
      birthdate: '',
      gender: '',
      address: '',
      mobile: '',
      datevisit: new Date().toISOString().split('T')[0],
      // Medical history fields - using boolean values
      has_dm: false,
      has_bp: false,
      has_tsh: false,
      has_acidity: false,
      has_allergic: false,
      allergic_details: '',
      has_others: false,
      others_details: ''
    });
  };

  // For pagination, get the current page's patients
  const patientsToShow = paginatedPatients();

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Patients
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPatient}
        >
          Add Patient
        </Button>
      </Box>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search by name, OPD, or mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ minWidth: 300 }}
            />
             {/* Date of Visit Filter */}
             <TextField
              label="Date of Visit"
              type="date"
              value={dateFilter !== undefined ? dateFilter : ''}
              onChange={(e) => setDateFilter(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ minWidth: 170 }}
            />
            <Button
              variant="outlined"
              onClick={() => setDateFilter('')}
              disabled={!dateFilter}
              sx={{ minWidth: 80 }}
            >
              Clear Date
            </Button>
            <Typography variant="body2" color="text.secondary">
              Total: {totalPatients} patients
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Patients Table */}
      <Paper sx={{ width: '100%', overflowX: 'auto', background: '#fff', borderRadius: 2, boxShadow: 3, border: '1.5px solid #e0e0e0' }}>
        <TableContainer sx={{ minWidth: 1100 }}>
          <Table stickyHeader sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <TableHead>
              <TableRow>
                {[
                  { label: 'OPD NO.', field: 'opd' },
                  { label: 'Name', field: 'name' },
                  { label: 'Age', field: 'age' },
                  { label: 'Gender', field: 'gender' },
                  { label: 'Contact No.', field: 'mobile' },
                  { label: 'Date of last Visit', field: 'last_visit' },
                  { label: 'Follow Up', field: 'followUp' },
                  { label: 'Total Visits', field: 'totalVisits' },
                  { label: 'Total Balance', field: 'totalBalance' },
                  { label: 'Total Credit', field: 'totalCredit' },
                ].map(col => (
                  <TableCell
                    key={col.field}
                    onClick={() => handleSort(col.field)}
                    title={`Sort by ${col.label}`}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#e3eafc',
                      border: '1px solid #bdbdbd',
                      borderBottomWidth: 2,
                      cursor: 'pointer',
                      userSelect: 'none',
                      minWidth: col.field === 'name' ? 160 : 100,
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {col.label}
                      {sortBy === col.field && (
                        sortOrder === 'ASC' ? (
                          <ArrowUpward fontSize="small" sx={{ color: '#1976d2', ml: 0.5 }} />
                        ) : (
                          <ArrowDownward fontSize="small" sx={{ color: '#1976d2', ml: 0.5 }} />
                        )
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : patientsToShow.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No patients found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                patientsToShow.map((patient: any, idx: number) => (
                  <TableRow
                    key={patient.id}
                    hover
                    sx={{
                      backgroundColor: idx % 2 === 0 ? '#f7fafd' : '#fff',
                      transition: 'background 0.2s',
                      '&:hover': { backgroundColor: '#e3eafc' },
                    }}
                  >
                    <TableCell sx={{ border: '1px solid #bdbdbd' }}>{patient.opd}</TableCell>
                    <TableCell sx={{ border: '1px solid #bdbdbd' }}>
                      <span
                        style={{ color: '#1976d2', fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}
                        onClick={() => navigate(`/patients/${patient.id}`)}
                        onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
                        title="View patient details"
                      >
                        {patient.name}
                      </span>
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #bdbdbd' }}>{patient.age}</TableCell>
                    <TableCell sx={{ border: '1px solid #bdbdbd' }}>{patient.gender}</TableCell>
                    <TableCell sx={{ border: '1px solid #bdbdbd' }}>{patient.mobile ? patient.mobile : '--'}</TableCell>
                    <TableCell sx={{ border: '1px solid #bdbdbd' }}>{patient.last_visit_date ? formatDate(patient.last_visit_date) : 'No visits'}</TableCell>
                    <TableCell sx={{ border: '1px solid #bdbdbd' }}>
                      {patient.followUp ? (() => {
                        // Check if follow-up is a valid date
                        const followDate = new Date(patient.followUp);
                        const isValidDate = !isNaN(followDate.getTime());
                        
                        // If it's not a valid date, show as text
                        if (!isValidDate) {
                          return (
                            <Typography variant="body2" align="center" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                              {patient.followUp}
                            </Typography>
                          );
                        }
                        
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        followDate.setHours(0, 0, 0, 0);
                        
                        // Check if patient has visited after the follow-up date
                        // For now, we'll assume if there's a last_visit_date after follow-up, they've visited
                        const hasVisitedAfterFollowUp = patient.last_visit_date && new Date(patient.last_visit_date) > followDate;
                        
                        // If patient has visited after follow-up date, show like date of visit
                        if (hasVisitedAfterFollowUp) {
                          return (
                            <Typography variant="body2" align="center">
                              {formatDate(`${followDate.getFullYear()}-${String(followDate.getMonth() + 1).padStart(2, '0')}-${String(followDate.getDate()).padStart(2, '0')}`)}
                            </Typography>
                          );
                        }
                        
                        const daysDiff = Math.ceil((followDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                        
                        let chipColor: 'success' | 'warning' | 'error' | 'default';
                        if (daysDiff >= 0) {
                          chipColor = 'success'; // Today or future - green
                        } else if (daysDiff >= -3) {
                          chipColor = 'warning'; // 1-3 days past - yellow
                        } else {
                          chipColor = 'error'; // More than 3 days past - red
                        }
                        
                        return (
                          <Chip 
                            label={formatDate(`${followDate.getFullYear()}-${String(followDate.getMonth() + 1).padStart(2, '0')}-${String(followDate.getDate()).padStart(2, '0')}`)} 
                            size="small" 
                            color={chipColor}
                            sx={{ fontWeight: 600 }}
                          />
                        );
                      })() : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No follow-up
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #bdbdbd' }}>{patient.totalVisits ?? '-'}</TableCell>
                    <TableCell sx={{ border: '1px solid #bdbdbd' }}>
                      {/* Show total balance in red if balance greater than 0 */}
                      {(
                        parseFloat(patient.totalBalance) > 0 ? ( 
                          <span style={{ color: '#d32f2f', fontWeight: 600 }}>
                            {patient.totalBalance}
                          </span>
                        ) : (
                          <span>{patient.totalBalance ?? '-'}</span>
                        )
                      )}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #bdbdbd' }}>{patient.totalCredit ?? '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100, 200]}
          component="div"
          count={totalPatients}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 50));
            setPage(0);
          }}
        />
      </Paper>

      {/* Add Patient Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle>Add New Patient</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="OPD Number"
                value={formData.opd}
                InputProps={{
                  readOnly: true,
                  style: { 
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                    color: '#1976d2'
                  }
                }}
                required
              />
              <TextField
                fullWidth
                label="Patient Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Birthdate (Optional)"
                type="date"
                value={formData.birthdate}
                onChange={(e) => handleBirthdateChange(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                label="Age"
                type="number"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                required
                disabled={!!formData.birthdate}
                helperText={formData.birthdate ? "Age calculated from birthdate" : "Enter age manually"}
              />
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={formData.gender}
                  label="Gender"
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                helperText="Optional"
              />
              <TextField
                fullWidth
                label="Date of First Visit"
                type="date"
                value={formData.datevisit}
                onChange={(e) => setFormData({ ...formData, datevisit: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>
            
            <TextField
              fullWidth
              label="Address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              multiline
              rows={3}
              helperText="Optional"
            />
            
            {/* Medical History Section */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
                Medical History
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Medical Conditions Pills */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  {[
                    { label: 'DM', field: 'has_dm' },
                    { label: 'BP', field: 'has_bp' },
                    { label: 'TSH', field: 'has_tsh' },
                    { label: 'Acidity', field: 'has_acidity' },
                    { label: 'Allergic', field: 'has_allergic' },
                    { label: 'Others', field: 'has_others' }
                  ].map((condition) => (
                    <Chip
                      key={condition.label}
                      label={condition.label}
                      variant={formData[condition.field as keyof typeof formData] ? "filled" : "outlined"}
                      color={formData[condition.field as keyof typeof formData] ? "primary" : "default"}
                      onClick={() => {
                        setFormData({
                          ...formData,
                          [condition.field]: !formData[condition.field as keyof typeof formData]
                        });
                      }}
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: 16,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: formData[condition.field as keyof typeof formData] ? '#1976d2' : '#f5f5f5'
                        }
                      }}
                    />
                  ))}
                </Box>
                
                {/* Medicine Details for Selected Conditions */}
                {(formData.has_allergic || formData.has_others) && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      Additional Details
                    </Typography>
                    
                    {formData.has_allergic && (
                      <TextField
                        fullWidth
                        label="Allergy Details"
                        value={formData.allergic_details}
                        onChange={(e) => setFormData({ ...formData, allergic_details: e.target.value })}
                        placeholder="Enter allergy details"
                      />
                    )}
                    
                    {formData.has_others && (
                      <TextField
                        fullWidth
                        label="Other Conditions/Medicines"
                        value={formData.others_details}
                        onChange={(e) => setFormData({ ...formData, others_details: e.target.value })}
                        placeholder="Enter other conditions or medicines"
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button onClick={handleSubmitPatient} variant="contained">Add Patient</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Add Patient */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Patients; 